import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import { ArrowRight, Star, ShieldCheck, Home as HomeIcon, Users, Plus, ExternalLink } from "lucide-react";
import { api, formatPrice } from "../lib/api";
import { EDGE_APP_URL } from "../lib/edge";
import SearchBar from "../components/SearchBar";
import PropertyCard from "../components/PropertyCard";
import MlsFeaturedCard from "../components/MlsFeaturedCard";
import FeaturedMlsCarousel from "../components/FeaturedMlsCarousel";
import Seo from "../components/Seo";
import { HAR_QUICK_CHIPS, citySlug, HAR_MARKET_GROUPS } from "../lib/harMarketAreas";

const HERO_IMG =
  "https://images.unsplash.com/photo-1749215695604-4d98a9338dec?crop=entropy&cs=srgb&fm=jpg&w=2000&q=85";

export default function Home() {
  const [featured, setFeatured] = useState([]);
  const [mlsFeatured, setMlsFeatured] = useState([]);
  const [neighborhoods, setNeighborhoods] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [stats, setStats] = useState(null);

  useEffect(() => {
    api.get("/properties", { params: { featured: true } }).then((r) => setFeatured(r.data));
    api.get("/neighborhoods").then((r) => setNeighborhoods(r.data));
    api.get("/testimonials").then((r) => setTestimonials(r.data));
    api.get("/stats").then((r) => setStats(r.data));
  }, []);

  // Live MLS luxury listings for the "Properties of distinction" grid — skip the first 6
  // that already appear in the top carousel so users see 12 unique listings on Home.
  // Auto-refreshes every 5 minutes so newly-synced luxury inventory surfaces without a reload.
  useEffect(() => {
    let cancelled = false;
    const fetchMls = () => {
      api
        .get("/mls/listings", {
          params: { min_price: 750000, status: "Active", limit: 6, skip: 6 },
        })
        .then((r) => { if (!cancelled) setMlsFeatured(r.data?.items || []); })
        .catch(() => { if (!cancelled) setMlsFeatured([]); });
    };
    fetchMls();
    const interval = setInterval(fetchMls, 5 * 60 * 1000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, []);

  return (
    <div data-testid="home-page">
      <Seo
        title="Houston Real Estate"
        description="Boutique residential brokerage representing buyers and sellers across Houston's most sought-after neighborhoods — River Oaks, The Heights, Memorial, Montrose, Midtown, and more."
        path="/"
      />
      {/* HERO */}
      <section className="relative h-[92vh] min-h-[680px] w-full overflow-hidden">
        <img
          src={HERO_IMG}
          alt="Houston skyline at golden hour"
          className="absolute inset-0 w-full h-full object-cover"
        />
        <div className="absolute inset-0 hero-gradient" />
        <div className="relative z-10 h-full max-w-7xl mx-auto px-6 lg:px-12 flex flex-col justify-end pb-20">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, ease: [0.22, 1, 0.36, 1] }}
          >
            <div className="flex items-center gap-3 mb-6 text-white/85">
              <div className="w-12 h-px bg-[#C9A961]" />
              <div className="eyebrow">Houston · Texas · Est. 2010</div>
            </div>
            <h1 className="font-display text-white text-6xl sm:text-7xl lg:text-8xl xl:text-[9rem] leading-[0.95] tracking-tight max-w-4xl font-light">
              Find the home <br />
              that fits <span className="serif-italic text-[#E63946]">your life.</span>
            </h1>
            <p className="text-white/85 text-base lg:text-lg max-w-xl mt-8 leading-relaxed font-light">
              A boutique residential brokerage representing buyers and sellers across Houston's most sought-after addresses.
            </p>
          </motion.div>

          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.9, delay: 0.2, ease: [0.22, 1, 0.36, 1] }}
            className="mt-12"
          >
            <SearchBar />
            {/* HAR market-area quick chips */}
            <div className="mt-5 flex flex-wrap gap-2 items-center" data-testid="home-quick-chips">
              <span className="text-[10px] uppercase tracking-[0.25em] text-white/70 mr-1">
                Popular areas:
              </span>
              {HAR_QUICK_CHIPS.slice(0, 12).map((c) => (
                <Link
                  key={c}
                  to={`/homes-for-sale/${citySlug(c)}`}
                  className="text-xs bg-white/10 hover:bg-white/20 backdrop-blur text-white px-3 py-1.5 rounded-full transition-colors border border-white/15"
                  data-testid={`home-chip-${c.toLowerCase().replace(/\s+/g, "-")}`}
                >
                  {c}
                </Link>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* STATS BAR */}
      {stats && (
        <section className="bg-white border-b border-gray-100">
          <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10 grid grid-cols-2 lg:grid-cols-4 gap-6 text-center lg:text-left">
            <Stat value={`${stats.years_serving}+`} label="Years in Houston" />
            <Stat value={`${stats.homes_sold}+`} label="Homes Sold" />
            <Stat value={`${stats.happy_clients}+`} label="Happy Families" />
            <Stat value="$1.2B" label="Closed Volume" />
          </div>
        </section>
      )}

      {/* FEATURED MLS CAROUSEL — 6 newest luxury listings ($750K+) from live Trestle feed */}
      <FeaturedMlsCarousel />

      {/* FEATURED PROPERTIES */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32" data-testid="home-featured">
        <SectionHead
          eyebrow="Curated · Houston"
          title={<>Properties of <span className="serif-italic text-[#E63946]">distinction.</span></>}
          right={
            <Link
              to="/mls-listings?min_price=750000"
              className="hidden md:inline-flex items-center gap-2 text-xs font-medium uppercase tracking-[0.25em] text-[#0A0A0A] editorial-link"
              data-testid="home-featured-viewall"
            >
              View all listings
            </Link>
          }
        />
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-x-8 gap-y-16 mt-16">
          {mlsFeatured.length > 0
            ? mlsFeatured.slice(0, 6).map((l) => (
                <MlsFeaturedCard key={l.listing_key} l={l} />
              ))
            : featured.slice(0, 6).map((p) => (
                <PropertyCard key={p.id} p={p} />
              ))}
          {mlsFeatured.length === 0 && featured.length === 0 && (
            <div className="col-span-full bg-white border border-dashed border-gray-300 rounded-xl py-16 px-6 text-center">
              <div className="w-12 h-12 rounded-full bg-[#E63946]/10 text-[#E63946] flex items-center justify-center mx-auto mb-4">
                <Plus className="w-5 h-5" strokeWidth={1.75} />
              </div>
              <div className="font-display text-2xl font-semibold text-[#1A1A1A]">
                Featured listings coming soon.
              </div>
              <p className="text-[#555] mt-2 max-w-md mx-auto">
                Fresh off-market homes and curated Houston listings will appear here. In the meantime, browse the full Houston MLS.
              </p>
              <Link
                to="/mls-listings"
                className="inline-block mt-6 bg-[#E63946] hover:bg-[#c9313d] text-white px-6 py-3 rounded-md font-semibold text-sm transition-colors"
                data-testid="home-featured-empty-mls"
              >
                Browse HAR MLS
              </Link>
            </div>
          )}
        </div>
        <div className="md:hidden mt-8">
          <Link
            to="/mls-listings?min_price=750000"
            className="inline-flex items-center gap-2 text-sm font-semibold text-[#E63946]"
          >
            View all listings <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>

      {/* WHY GENS */}
      <section className="bg-white border-y border-stone-200">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32 grid grid-cols-1 lg:grid-cols-12 gap-16 items-start">
          <div className="lg:col-span-5">
            <div className="flex items-center gap-3 mb-4">
              <div className="w-8 h-px bg-[#C9A961]" />
              <div className="eyebrow text-[#0A0A0A]">Our Approach</div>
            </div>
            <h2 className="font-display text-5xl lg:text-6xl leading-[0.95] tracking-tight text-[#0A0A0A] font-light">
              White-glove service, <span className="serif-italic text-[#E63946]">quietly relentless.</span>
            </h2>
            <p className="text-[#444] leading-relaxed mt-7 max-w-md font-light text-lg">
              We're a boutique team of Houston natives and longtime locals. We know the difference between a Heights bungalow and a Heights builder spec — and we'll make sure you do too.
            </p>
          </div>
          <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 gap-5">
            <Feature
              icon={<HomeIcon className="w-5 h-5" />}
              title="Hyperlocal expertise"
              text="River Oaks to The Woodlands — we sell where we live."
            />
            <Feature
              icon={<ShieldCheck className="w-5 h-5" />}
              title="Honest counsel"
              text="No pushy upsells. We'll tell you when not to buy."
            />
            <Feature
              icon={<Users className="w-5 h-5" />}
              title="Client-first team"
              text="A small bench of senior agents — never handed off."
            />
            <Feature
              icon={<Star className="w-5 h-5" />}
              title="Marketing that sells"
              text="Cinematic media, targeted reach, real results."
            />
          </div>
        </div>
      </section>

      {/* NEIGHBORHOODS BENTO */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32" data-testid="home-neighborhoods">
        <SectionHead
          eyebrow="By Neighborhood"
          title={<>Where do you want to <span className="serif-italic text-[#E63946]">live?</span></>}
        />
        <div className="grid grid-cols-1 md:grid-cols-12 gap-3 mt-16">
          {neighborhoods.slice(0, 5).map((n, i) => {
            const span =
              i === 0
                ? "md:col-span-7 md:row-span-2 aspect-[16/10] md:aspect-auto md:h-[34rem]"
                : i === 1
                ? "md:col-span-5 aspect-[4/3] md:aspect-auto md:h-[16.5rem]"
                : i === 2
                ? "md:col-span-5 aspect-[4/3] md:aspect-auto md:h-[16.5rem]"
                : i === 3
                ? "md:col-span-6 aspect-[4/3] md:aspect-auto md:h-[18rem]"
                : "md:col-span-6 aspect-[4/3] md:aspect-auto md:h-[18rem]";
            return (
              <Link
                key={n.id}
                to={`/neighborhoods/${n.slug}`}
                className={`relative group overflow-hidden ${span}`}
                data-testid={`home-nbhd-${n.slug}`}
              >
                <img
                  src={n.image}
                  alt={n.name}
                  className="absolute inset-0 w-full h-full object-cover img-zoom"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-black/80 via-black/30 to-transparent" />
                <div className="absolute inset-x-0 bottom-0 p-7 lg:p-9 text-white">
                  <div className="eyebrow opacity-80">
                    {formatPrice(n.median_price)} median
                  </div>
                  <div className="font-display text-3xl lg:text-4xl mt-2 leading-tight">
                    {n.name}
                  </div>
                  <div className="text-sm opacity-90 mt-1 max-w-md font-light">{n.tagline}</div>
                </div>
              </Link>
            );
          })}
        </div>
      </section>

      {/* BROWSE BY MARKET AREA — SEO landing pages */}
      <section className="bg-white border-t border-stone-200" data-testid="home-market-areas">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-24">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#C9A961]" />
            <div className="eyebrow text-[#0A0A0A]">Browse by Market Area</div>
          </div>
          <h2 className="font-display text-4xl lg:text-5xl leading-[0.95] font-light max-w-2xl">
            Every Houston-metro city, <span className="serif-italic text-[#E63946]">covered.</span>
          </h2>
          <p className="text-[#555] mt-5 max-w-xl font-light">
            Live HAR MLS inventory for 68+ Greater Houston markets. Median price, active count, and every home currently on the market.
          </p>
          <div className="mt-12 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-8">
            {HAR_MARKET_GROUPS.map((g) => (
              <div key={g.label} data-testid={`market-group-${g.label.toLowerCase().replace(/\s+/g, "-")}`}>
                <div className="text-[10px] uppercase tracking-[0.2em] text-gray-400 mb-3 flex items-center gap-2">
                  <span className="w-1.5 h-1.5 rounded-full bg-[#E63946]" />
                  {g.label}
                </div>
                <ul className="space-y-1.5">
                  {g.cities.map((c) => (
                    <li key={c}>
                      <Link
                        to={`/homes-for-sale/${citySlug(c)}`}
                        className="text-sm text-[#1A1A1A] hover:text-[#E63946] hover:underline underline-offset-2 transition-colors"
                        data-testid={`market-city-${citySlug(c)}`}
                      >
                        {c}, TX
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* MORTGAGE / EDGE HOME FINANCE */}
      <section className="bg-stone-100/60 border-y border-gray-100">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-[#E63946] mb-4">
              One person · Both sides
            </div>
            <h2 className="font-display text-4xl lg:text-5xl font-light tracking-tighter leading-[1.05] text-[#1A1A1A]">
              Search homes <em className="not-italic">and</em> finance them — with the same trusted advisor.
            </h2>
            <p className="text-[#555] leading-relaxed mt-6 max-w-xl">
              Pierre Joseph is a licensed Texas Realtor (#0537207) at Gens Realty AND a licensed Loan Officer (NMLS #1978260)
              at Edge Home Finance. Pre-approval, property tours, and closing — handled by one person who knows
              every part of your deal.
            </p>
            <div className="flex flex-wrap gap-3 mt-8">
              <a
                href={EDGE_APP_URL}
                target="_blank"
                rel="noopener noreferrer"
                className="bg-[#E63946] hover:bg-[#c9313d] text-white px-6 py-3 rounded-md font-semibold text-sm inline-flex items-center gap-2"
                data-testid="home-mortgage-cta"
              >
                Apply Online (1003) <ExternalLink className="w-4 h-4" />
              </a>
              <Link
                to="/mortgage"
                className="border border-[#1A1A1A] text-[#1A1A1A] hover:bg-[#1A1A1A] hover:text-white px-6 py-3 rounded-md font-semibold text-sm transition-colors"
              >
                Explore loan programs
              </Link>
            </div>
          </div>
          <div className="lg:col-span-5">
            <div className="bg-white border border-gray-200 rounded-2xl p-7">
              <div className="flex items-center gap-3 mb-5">
                <div className="bg-[#1E3A6D] rounded p-1.5">
                  <img
                    src="https://customer-assets.emergentagent.com/job_tx-property-hub/artifacts/et7sgymq_Edge-home-main-logo.png"
                    alt="Edge Home Finance"
                    className="h-7 w-auto"
                  />
                </div>
                <div>
                  <div className="font-display font-semibold text-[#1A1A1A]">Edge Home Finance</div>
                  <div className="text-[10px] uppercase tracking-[0.2em] text-gray-500">NMLS #891464</div>
                </div>
              </div>
              <div className="space-y-3 text-sm text-[#333]">
                <Item>Conventional, FHA, VA, Jumbo, USDA</Item>
                <Item>First-time buyer & down-payment assistance</Item>
                <Item>Refinance & Texas-compliant cash-out</Item>
                <Item>24-hour pre-approval decisions</Item>
              </div>
              <div className="mt-6 pt-6 border-t border-gray-100 text-xs text-gray-500">
                Pierre Joseph · NMLS #1978260 · 832-741-4662
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* HOME VALUE CTA */}
      <section className="relative bg-[#E63946] overflow-hidden">
        <div className="max-w-7xl mx-auto px-6 lg:px-10 py-24 grid grid-cols-1 lg:grid-cols-12 gap-10 items-center">
          <div className="lg:col-span-7">
            <div className="text-[10px] uppercase tracking-[0.3em] text-white/80 mb-4">
              Selling? Start here.
            </div>
            <h2 className="font-display text-4xl lg:text-6xl font-light tracking-tighter text-white leading-[1.05]">
              What's your Houston home worth today?
            </h2>
            <p className="text-white/90 mt-5 max-w-xl text-lg">
              Get an instant HAR-powered estimate — then let's refine it together with a no-pressure walkthrough.
            </p>
          </div>
          <div className="lg:col-span-5 lg:text-right">
            <Link
              to="/home-value"
              className="inline-block bg-white text-[#E63946] hover:bg-stone-100 px-8 py-4 rounded-md font-semibold text-sm transition-colors"
              data-testid="home-cta-homevalue"
            >
              Get my home value →
            </Link>
          </div>
        </div>
      </section>

      {/* TESTIMONIALS */}
      <section className="bg-[#0A0A0A] text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-12 py-32">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-8 h-px bg-[#C9A961]" />
            <div className="eyebrow text-white/80">Client Stories</div>
          </div>
          <h2 className="font-display text-5xl lg:text-6xl leading-[0.95] tracking-tight max-w-2xl font-light">
            Houstonians who trusted us with the <span className="serif-italic text-[#E63946]">move.</span>
          </h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-8 mt-16">
            {testimonials.map((t) => (
              <div
                key={t.id}
                className="border-t border-white/15 pt-8"
                data-testid={`testimonial-${t.id}`}
              >
                <div className="flex gap-1 mb-4">
                  {Array.from({ length: t.rating }).map((_, i) => (
                    <Star key={i} className="w-3.5 h-3.5 fill-[#C9A961] text-[#C9A961]" />
                  ))}
                </div>
                <p className="font-display text-2xl lg:text-3xl leading-snug text-white/95 mb-7 font-light">
                  "{t.text}"
                </p>
                <div className="text-sm font-medium tracking-wide">{t.name}</div>
                <div className="eyebrow text-white/50 mt-1">{t.location}</div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-6 lg:px-12 py-32 text-center">
        <div className="gold-rule w-24 mx-auto mb-10" />
        <h2 className="font-display text-5xl lg:text-7xl leading-[0.95] tracking-tight font-light">
          Ready to find your <br /><span className="serif-italic text-[#E63946]">Houston home?</span>
        </h2>
        <p className="text-[#555] max-w-xl mx-auto mt-7 text-lg font-light">
          Whether you're buying, selling, or just exploring — let's start a conversation. No pressure, all signal.
        </p>
        <div className="flex flex-wrap items-center justify-center gap-4 mt-10">
          <Link
            to="/contact"
            className="bg-[#0A0A0A] hover:bg-[#E63946] text-white px-8 py-4 font-medium text-xs uppercase tracking-[0.25em] transition-colors"
            data-testid="home-cta-contact"
          >
            Schedule a Consultation
          </Link>
          <Link
            to="/properties"
            className="border border-[#0A0A0A] text-[#0A0A0A] hover:bg-[#0A0A0A] hover:text-white px-8 py-4 font-medium text-xs uppercase tracking-[0.25em] transition-colors"
            data-testid="home-cta-browse"
          >
            Browse all Listings
          </Link>
        </div>
      </section>
    </div>
  );
}

function SectionHead({ eyebrow, title, right }) {
  return (
    <div className="flex items-end justify-between gap-6 flex-wrap">
      <div>
        <div className="flex items-center gap-3 mb-4">
          <div className="w-8 h-px bg-[#C9A961]" />
          <div className="eyebrow text-[#0A0A0A]">{eyebrow}</div>
        </div>
        <h2 className="font-display text-5xl lg:text-6xl leading-[0.95] tracking-tight text-[#0A0A0A] font-light max-w-2xl">
          {title}
        </h2>
      </div>
      {right}
    </div>
  );
}

function Stat({ value, label }) {
  return (
    <div>
      <div className="font-display serif-italic text-5xl lg:text-6xl text-[#E63946] leading-none">
        {value}
      </div>
      <div className="eyebrow text-gray-500 mt-2">
        {label}
      </div>
    </div>
  );
}

function Feature({ icon, title, text }) {
  return (
    <div className="bg-stone-50 border border-stone-200 p-7">
      <div className="w-10 h-10 bg-[#E63946]/10 text-[#E63946] flex items-center justify-center mb-5">
        {icon}
      </div>
      <div className="font-display text-2xl text-[#0A0A0A]">{title}</div>
      <p className="text-sm text-[#555] mt-2 leading-relaxed font-light">{text}</p>
    </div>
  );
}

function Item({ children }) {
  return (
    <div className="flex items-start gap-2.5">
      <span className="w-5 h-5 rounded-full bg-[#1E3A6D]/10 text-[#1E3A6D] flex items-center justify-center shrink-0 mt-0.5">
        <svg className="w-3 h-3" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round"><polyline points="20 6 9 17 4 12" /></svg>
      </span>
      {children}
    </div>
  );
}
